import React, { useState, useRef, useEffect, RefObject } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Zoom, Thumbs } from "swiper/modules"; // Import Thumbs module
import Lightbox from "yet-another-react-lightbox";
import LightboxZoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import type { Swiper as SwiperType } from "swiper";
import Skeleton from "react-loading-skeleton";

// Import lightbox styles
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/zoom";
import "swiper/css/thumbs"; // Import Thumbs CSS

// Import required types and helpers
import type {
  TransformedProduct,
  TransformedVariation,
} from "../../types/productApiTypes";
import type { RawImage } from "../../../../types"; // Use RawImage as it's what the helper expects/returns
import {
  getGalleryImages,
  isGalleryEmpty,
  isValidImage,
  PLACEHOLDER_IMAGE_URL,
} from "../../helpers/productImgHelpers";

// --- Props ---
interface ProductGalleryProps {
  product: TransformedProduct;
  selectedVariation: TransformedVariation | null;
  variationsLoading?: boolean; // Includes product loading + variation selection readiness
}

// --- Lightbox Slide Type ---
interface LightboxSlide {
  src: string;
  alt: string;
  // Add other properties like title, description if needed by lightbox plugins
}

// --- Component ---
const ProductGallery: React.FC<ProductGalleryProps> = ({
  product,
  selectedVariation = null,
  variationsLoading = false, // Combined loading state from parent
}) => {
  // --- Hooks ---
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);
  const mainSwiperRef = useRef<{ swiper: SwiperType } | null>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);

  // --- Image Calculation ---
  // Get the effective gallery images based on product/variation/loading state
  const galleryImages: RawImage[] = getGalleryImages(
    product.images || [], // Pass parent images
    selectedVariation,
    variationsLoading // Pass loading state
  );

  // Create lightbox slides *only* from valid images
  const lightboxSlides: LightboxSlide[] = galleryImages
    .filter(isValidImage) // Filter out invalid image objects
    .map((image) => ({
      src: image.src,
      alt: image.alt || product.name || "Product image", // Use product name as fallback alt
    }));

  // --- Event Handlers ---
  const handleZoomClick = (index: number): void => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleThumbnailClick = (index: number): void => {
    mainSwiperRef.current?.swiper?.slideTo(index);
  };

  // --- Effects ---
  // Update main swiper and thumbs when variation changes or galleryImages updates
  useEffect(() => {
    const mainSwiperInstance = mainSwiperRef.current?.swiper;
    if (mainSwiperInstance && !mainSwiperInstance.destroyed) {
      // Slide to the first image smoothly when the effective gallery changes
      // Only do this if not actively loading to avoid jumps during loading phase
      if (!variationsLoading) {
        mainSwiperInstance.slideTo(0, 300); // Slide to first image
        mainSwiperInstance.update(); // Force update Swiper instance
      }
    }
    // Also reset thumbs swiper active slide
    if (thumbsSwiper && !thumbsSwiper.destroyed) {
      if (!variationsLoading) {
        thumbsSwiper.slideTo(0, 300);
        thumbsSwiper.update(); // Force update thumbs Swiper
      }
    }
    // Dependencies: selectedVariation ID to trigger on change,
    // galleryImages length as a proxy for content change,
    // and the swiper instances themselves.
  }, [
    selectedVariation?.id,
    galleryImages.length,
    variationsLoading,
    thumbsSwiper,
  ]);

  // --- Conditional Returns ---

  // 1. Loading Skeleton
  // Show skeleton if the parent indicates loading (product fetch, variation selection, translation ready state)
  if (variationsLoading) {
    return (
      <div className="product-gallery product-gallery-loading">
        {/* Main Image Skeleton */}
        <Skeleton height={450} className="gallery-skeleton" />
        {/* Thumbnails Skeleton */}
        <div className="thumbnails-skeleton">
          <Skeleton
            height={80}
            width={80}
            count={4} // Show a few thumbnail skeletons
            inline={true}
            style={{ marginRight: "10px" }}
          />
        </div>
      </div>
    );
  }

  // 2. Empty Gallery Placeholder
  // Check if the final calculated gallery is empty
  if (isGalleryEmpty(galleryImages)) {
    return (
      <div className="product-gallery product-gallery-empty">
        <div className="main-image-container">
          {/* Display a placeholder image */}
          <img
            src={PLACEHOLDER_IMAGE_URL}
            alt={`${product.name || "Product"} placeholder`}
            className="placeholder-image" // Add class for styling
          />
        </div>
        {/* Optionally hide thumbnails container or show placeholder thumbs */}
        <div className="thumbnails-container is-empty">
          {/* Content for empty thumbnails (if any) */}
        </div>
      </div>
    );
  }

  // 3. Render Main Gallery
  return (
    <div className="product-gallery">
      {/* Main Image Swiper */}
      <div className="main-image-container">
        <Swiper
          ref={mainSwiperRef as RefObject<any>} // Correct ref typing might be needed
          modules={[Navigation, Zoom, Thumbs]}
          spaceBetween={10}
          navigation={galleryImages.length > 1} // Only show nav if multiple images
          className="main-swiper"
          thumbs={{
            // Ensure thumbsSwiper exists and is not destroyed before passing
            swiper:
              thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
          }}
          zoom={{ maxRatio: 2, toggle: true }} // Enable zoom functionality
          loop={galleryImages.length > 1} // Enable loop only if multiple images
        >
          {galleryImages.filter(isValidImage).map((image, index) => (
            <SwiperSlide
              key={
                image.id ? `gallery-${image.id}-${index}` : `fallback-${index}`
              }
            >
              <div className="swiper-zoom-container">
                <img
                  src={image.src}
                  alt={
                    image.alt || product.name || `Product image ${index + 1}`
                  }
                  loading={index === 0 ? "eager" : "lazy"} // Load first image eagerly
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Zoom button - triggers lightbox */}
        <button
          className="zoom-button"
          title="View full size"
          onClick={
            () => handleZoomClick(mainSwiperRef.current?.swiper?.realIndex ?? 0) // Use realIndex for loops
          }
          aria-label="View image larger"
        >
          {/* Simple zoom icon SVG */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zM10 7H9v2H7v1h2v2h1v-2h2V9h-2z" />
          </svg>
        </button>
      </div>

      {/* Thumbnail Swiper (conditionally rendered) */}
      {galleryImages.length > 1 && (
        <div className="thumbnails-container">
          <Swiper
            onSwiper={setThumbsSwiper} // Capture thumbs instance
            spaceBetween={10}
            slidesPerView={4}
            freeMode={false} // Disable free mode for better control
            watchSlidesProgress={true}
            modules={[Thumbs]}
            className="thumbnail-swiper"
            loop={false} // Thumbnails usually don't need looping
            breakpoints={{
              768: { slidesPerView: 5 },
              1024: { slidesPerView: 6 },
            }}
          >
            {galleryImages.filter(isValidImage).map((image, index) => (
              <SwiperSlide
                key={
                  image.id
                    ? `thumb-${image.id}-${index}`
                    : `fallback-thumb-${index}`
                }
                onClick={() => handleThumbnailClick(index)} // Add onClick handler
                role="button" // Indicate clickable
                tabIndex={0} // Make focusable
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    handleThumbnailClick(index);
                }} // Keyboard interaction
                aria-label={`View image ${index + 1}`}
              >
                <img
                  src={image.thumbnail || image.src} // Use thumbnail if available
                  alt={
                    image.alt ||
                    product.name ||
                    `Product thumbnail ${index + 1}`
                  }
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {/* Lightbox Component */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={lightboxSlides} // Pass the prepared slides
        plugins={[LightboxZoom, Thumbnails]} // Enable plugins
        zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }} // Configure zoom
        thumbnails={{ position: "bottom", width: 80, height: 80 }} // Configure thumbnails
        styles={{
          // Optional custom styling
          container: { backgroundColor: "rgba(0, 0, 0, 0.9)" },
          thumbnailsContainer: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
        }}
        render={{
          // Optional: Custom icon rendering
          iconClose: () => (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="white"
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          ),
        }}
      />
    </div>
  );
};

export default ProductGallery;
